import React from 'react';
import {
  Plus, Check, Package,
  Search, Globe, BarChart2, DatabaseZap,
  Paintbrush, LayoutTemplate, Wrench, MonitorPlay, Component, ScatterChart,
  GitPullRequest, BookOpen, BookMarked, Bug, MessageSquareCode, Layers,
  GitCompare, Table2, FlaskConical, AlertTriangle, PenLine, Regex,
  Megaphone, MailOpen, Palette, TrendingUp,
  FolderKanban, CalendarDays, Webhook, Brain,
  Sheet, FileBarChart,
  Wand2, Frame, GalleryHorizontal, ImagePlus, Figma, ClipboardList,
  UserCircle, Type, ScanEye, Tag, BookCopy, SplitSquareHorizontal,
  Smartphone, Accessibility, Info,
  Twitter, Linkedin, Instagram, Bell,
  FileText, FileCog, Presentation, ScanText,
  Send, RefreshCw, ShieldCheck, FileSignature,
  Ticket, Lightbulb, HelpCircle, Heart,
  Scroll, FileSearch, Handshake,
  ListChecks, CheckSquare,
  Stethoscope, CalendarClock, ClipboardPlus, FilePlus2, Forward, Activity,
  Siren, ClipboardEdit, GraduationCap,
  Briefcase, Mic, MailCheck, ClipboardCheck, BarChart3, BookOpenCheck,
  LogOut, Users, Users2, MessageCircle, CircleHelp,
  Mail, Rocket, Flame, BrainCircuit, Map, Newspaper, FileCheck, Compass,
  DollarSign, Zap, Code2,
  type LucideIcon,
} from 'lucide-react';
import { WS, type SkillItem, SKILL_DOMAINS } from './workspaceData.ts';
import { alpha } from '../../utils/alpha';

// Map icon string names (from SKILL_LIBRARY.icon) → Lucide component
// Exported so other workspace components (e.g. SkillPill) can reuse the same map
export const SKILL_ICONS: Record<string, LucideIcon> = {
  Search, Globe, BarChart2, DatabaseZap,
  Paintbrush, LayoutTemplate, Wrench, MonitorPlay, Component, ScatterChart,
  GitPullRequest, BookOpen, BookMarked, Bug, MessageSquareCode, Layers,
  GitCompare, Table2, FlaskConical, AlertTriangle, PenLine, Regex,
  Megaphone, MailOpen, Palette, TrendingUp,
  FolderKanban, CalendarDays, Webhook, Brain,
  Sheet, FileBarChart,
  Wand2, Frame, GalleryHorizontal, ImagePlus, Figma, ClipboardList,
  UserCircle, Type, ScanEye, Tag, BookCopy, SplitSquareHorizontal,
  Smartphone, Accessibility, Info,
  Twitter, Linkedin, Instagram, Bell,
  FileText, FileCog, Presentation, ScanText,
  Send, RefreshCw, ShieldCheck, FileSignature,
  Ticket, Lightbulb, HelpCircle, Heart,
  Scroll, FileSearch, Handshake,
  ListChecks, CheckSquare,
  Stethoscope, CalendarClock, ClipboardPlus, FilePlus2, Forward, Activity,
  Siren, ClipboardEdit, GraduationCap,
  Briefcase, Mic, MailCheck, ClipboardCheck, BarChart3, BookOpenCheck,
  LogOut, Users, Users2, MessageCircle, CircleHelp,
  Mail, Rocket, Flame, BrainCircuit, Map, Newspaper, FileCheck, Compass,
  DollarSign, Zap, Code2,
  // Aliases for icon names used in SKILL_LIBRARY that differ from lucide exports
  PresentationChart: BarChart2,
};

interface SkillCardProps {
  skill: SkillItem;
  isActive: boolean;
  onToggle: (id: string) => void;
  compact?: boolean;
  animate?: boolean;
}

export default function SkillCard({ skill, isActive, onToggle, compact = false, animate = false }: SkillCardProps) {
  const [hovered, setHovered] = React.useState(false);
  const domain = SKILL_DOMAINS.find(d => d.id === skill.domain);
  const accentColor = domain?.color || WS.accent;
  const accentBg = domain?.colorBg || WS.accentBg;
  const SkillIcon = SKILL_ICONS[skill.icon] || Package;

  return (
    <div
      className={`ws-skill-card ws-hover-scale ${animate ? 'ws-card-animate' : ''}`}
      onClick={() => onToggle(skill.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: compact ? 'center' : 'flex-start',
        gap: 10,
        padding: compact ? '8px 10px' : '11px 12px',
        borderRadius: 12,
        cursor: 'pointer',
        border: `1px solid ${isActive ? `${alpha(accentColor, '40')}` : hovered ? WS.border3 : WS.border}`,
        background: isActive
          ? `linear-gradient(135deg, ${accentBg} 0%, rgba(255,255,255,0.02) 100%)`
          : hovered
          ? WS.surface3
          : WS.surface2,
        transition: 'all 0.15s ease',
        boxShadow: isActive ? `0 0 0 1px ${alpha(accentColor, '20')}, 0 2px 8px rgba(0,0,0,0.2)` : 'none',
        userSelect: 'none',
      }}
    >
      {/* Icon */}
      <div style={{
        width: compact ? 28 : 32,
        height: compact ? 28 : 32,
        borderRadius: 8,
        flexShrink: 0,
        background: isActive ? accentBg : 'rgba(255,255,255,0.04)',
        border: `1px solid ${isActive ? `${alpha(accentColor, '30')}` : WS.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.15s',
      }}>
        <SkillIcon size={compact ? 14 : 16} color={isActive ? accentColor : WS.ink3} strokeWidth={1.75} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: compact ? 0 : 3,
        }}>
          <span style={{
            fontSize: compact ? 12 : 12.5,
            fontWeight: 500,
            color: isActive ? accentColor : WS.ink,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {skill.name}
          </span>
          {skill.official && (
            <span style={{
              fontSize: 9,
              fontWeight: 600,
              padding: '1px 5px',
              borderRadius: 99,
              background: `${alpha(accentColor, '18')}`,
              color: accentColor,
              border: `1px solid ${alpha(accentColor, '30')}`,
              flexShrink: 0,
              letterSpacing: '0.04em',
            }}>
              official
            </span>
          )}
        </div>

        {!compact && (
          <div style={{
            fontSize: 11,
            color: WS.ink3,
            lineHeight: 1.5,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}>
            {skill.description}
          </div>
        )}

        {!compact && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 6,
          }}>
            {skill.tags.slice(0, 2).map(tag => (
              <span key={tag} style={{
                fontSize: 10,
                color: WS.ink4,
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${WS.border}`,
                padding: '1px 6px',
                borderRadius: 99,
              }}>
                {tag}
              </span>
            ))}
            <span style={{ fontSize: 10, color: WS.ink4, marginLeft: 'auto' }}>
              ★ {skill.uses}
            </span>
          </div>
        )}
      </div>

      {/* Add/Check button */}
      <div
        className="ws-skill-add"
        style={{
          width: compact ? 22 : 24,
          height: compact ? 22 : 24,
          borderRadius: 6,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: isActive ? accentColor : hovered ? 'rgba(255,255,255,0.08)' : 'transparent',
          border: `1px solid ${isActive ? 'transparent' : hovered ? WS.border3 : WS.border}`,
          transition: 'all 0.15s',
          opacity: isActive ? 1 : hovered ? 1 : 0,
          transform: isActive ? 'scale(1)' : 'scale(0.9)',
        }}
      >
        {isActive
          ? <Check size={11} color="var(--app-on-accent)" strokeWidth={2.5} />
          : <Plus size={11} color={WS.ink3} strokeWidth={2.5} />
        }
      </div>
    </div>
  );
}
